import type { BusinessCustomerSummary, CustomerProfile } from '../api/schemas';
import { customerStatus, monthYear, relativeVisit } from '../lib/customers';
import { LEVEL_LABEL, panamaToday, pauseDayLabel } from '../lib/customerFile';
import { initialsOf } from '../lib/whoami';

type ClientCardProps = {
  customer: BusinessCustomerSummary;
  now: Date;
  profile: CustomerProfile | undefined;
  /** Paused today (a trip): reads En pausa instead of Inactivo. */
  paused: boolean;
  graded: boolean;
  onOpen: () => void;
};

/** What kind of client this is, in one line under the name. */
function kindOf(label: string | undefined): string {
  switch (label) {
    case 'Con cita':
      return 'Cliente con cita';
    case 'Nuevo':
      return 'Cliente nuevo';
    case 'Regular':
      return 'Cliente recurrente';
    case 'Inactivo':
      return 'Hace tiempo que no viene';
    case 'En pausa':
      return 'Cliente en pausa';
    default:
      return 'Cliente';
  }
}

/** The one thing worth doing next, from what the directory already knows. */
function nextStepOf(label: string | undefined): string {
  switch (label) {
    case 'Con cita':
      return 'Tiene su próxima visita agendada.';
    case 'Nuevo':
      return 'Primera visita reciente: que se sienta bienvenido.';
    case 'Regular':
      return 'Viene seguido. Al día.';
    case 'Inactivo':
      return 'Más de 60 días sin venir: buen momento para invitarlo.';
    case 'En pausa':
      return 'En pausa por ahora: no cuenta como inactivo.';
    default:
      return 'Sin pendientes.';
  }
}

/** A client as a card (the Clientes grid): who, the facts, what is next, and the way into the ficha. */
export function ClientCard({ customer, now, profile, paused, graded, onOpen }: ClientCardProps) {
  const status = customerStatus(customer, now, paused);
  const pausedUntil =
    status?.label === 'En pausa' && profile?.pause
      ? `En pausa hasta ${pauseDayLabel(profile.pause.until, panamaToday(now))}: no cuenta como inactivo.`
      : null;
  const level = graded && profile?.level ? LEVEL_LABEL[profile.level] : null;
  // A visit dated ahead of today is the next one, not the last.
  const upcoming = new Date(customer.lastVisitAt).getTime() > now.getTime();
  return (
    <li className="client-card">
      <div className="client-card-head">
        <span aria-hidden="true" className="client-avatar">
          {initialsOf(customer.displayName)}
        </span>
        <span className="client-card-titles">
          <span className="row-title">{customer.displayName}</span>
          <span className="client-card-kind">{kindOf(status?.label)}</span>
        </span>
        {level ? <span className="client-level-word">{level}</span> : null}
      </div>
      <dl className="client-card-facts">
        <div>
          <dt>Visitas</dt>
          <dd>{customer.totalVisits}</dd>
        </div>
        {upcoming ? (
          <div>
            <dt>Próxima visita</dt>
            <dd>Agendada</dd>
          </div>
        ) : (
          <div>
            <dt>Última visita</dt>
            <dd>{relativeVisit(customer.lastVisitAt, now)}</dd>
          </div>
        )}
        <div>
          <dt>Cliente desde</dt>
          <dd>{monthYear(customer.firstVisitAt)}</dd>
        </div>
        <div>
          <dt>Teléfono</dt>
          <dd>{customer.maskedPhone ?? 'Sin teléfono'}</dd>
        </div>
      </dl>
      <p className="client-card-next">
        {status ? (
          <span className="status" data-tone={status.tone}>
            {status.label}
          </span>
        ) : null}
        <span>{pausedUntil ?? nextStepOf(status?.label)}</span>
      </p>
      <div className="client-card-actions">
        <button type="button" className="btn small" aria-label={`Ver ficha de ${customer.displayName}`} onClick={onOpen}>
          Ver ficha
        </button>
      </div>
    </li>
  );
}
